import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { KeyValueData } from '../../../domains/common.domain';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ValidationsService } from '../../../services/validations.service';

import { PersonalDataService } from '../personal-data.service';
import { ApplicationSectionKeys, InstallationFlowType } from '../../../constants/app.constants';
import { ConnectionObjectFormData, LookupOptionsData } from '../../jurisdiction.domain';
import { AllDropdownKeys } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { EnvironmentService } from '../../../services/environment.service';
import { SectionIdentifier, SectionReview } from 'src/app/+shared/components/review-section/review-section.domain';
import { ReviewSectionSelectors } from 'src/app/+shared/components/review-section/review-section.selectors';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-connection-address',
  styleUrls: ['./connection-address.component.scss'],
  templateUrl: './connection-address.component.html',
})
export class ConnectionAddressComponent implements OnInit {
  @Input() applicationId: number;
  @Input() connectionData: ConnectionObjectFormData;
  @Input() isReadOnly: boolean = false;
  @Input() set areLocationFieldsHidden(areLocationFieldsHidden: boolean) {
    this.areAddressFieldsMandatory = areLocationFieldsHidden;

    const cityControl = this.connectionAddressFG?.get('city');
    cityControl?.setValidators(areLocationFieldsHidden ? [Validators.required] : []);
    this.connectionAddressFG?.controls?.city.updateValueAndValidity();

    const streetControl = this.connectionAddressFG?.get('street');
    streetControl?.setValidators(areLocationFieldsHidden ? [Validators.required] : []);
    this.connectionAddressFG?.controls?.street.updateValueAndValidity();

    this.connectionAddressFG?.controls?.streetNo?.setValidators(
      areLocationFieldsHidden
        ? [Validators.required, ValidationsService.streetNumberValidator()]
        : [ValidationsService.streetNumberValidator()]
    );
    this.connectionAddressFG?.controls?.streetNo?.updateValueAndValidity();

    this.connectionAddressFG?.controls?.postCode?.setValidators(
      areLocationFieldsHidden ? [Validators.required, ValidationsService.maxLengthValidator()] : [ValidationsService.maxLengthValidator()]
    );
    this.connectionAddressFG?.controls?.postCode?.updateValueAndValidity();
  }

  areAddressFieldsMandatory: boolean;
  isParcelAddress: boolean;

  buildingTypeKey: string = AllDropdownKeys.DevelopmentAreaType;
  buildingTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.buildingTypeKey);

  currentInstallationFlow: string = this.localStorageService.getItem('installation_flow') || '';

  infoBubblesData: {
    [key: string]: boolean;
  } = this.environmentService.getInfoBubblesData('personalData') || null;

  installationFlowType: KeyValueData = InstallationFlowType;

  connectionAddressFG: FormGroup;

  applicationSection = ApplicationSectionKeys.personalData.connectionAddress;

  get showNewDevAreaDropdownForConnection(): boolean {
    return (
      this.environmentService.showNewDevAreaDropdownForConnection &&
      (this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
        this.currentInstallationFlow === this.installationFlowType.TemporaryConnection)
    );
  }

  get showNewDevAreaDropdownForInstallation(): boolean {
    return (
      this.environmentService.showNewDevAreaDropdownForInstallation &&
      (this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
        this.currentInstallationFlow === this.installationFlowType.UpdateInstallation)
    );
  }

  constructor(
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private personalDataService: PersonalDataService,
    private reviewSectionSelectors: ReviewSectionSelectors
  ) {}

  ngOnInit(): void {
    const locationData = this.localStorageService.getItem('location_data') ?? {};

    this.isParcelAddress = locationData?.noAddressAvailable || (this.isReadOnly && this.connectionData?.swsParcel) || false;

    if (locationData.postcode) {
      locationData.postcode = locationData.postcode.length > 5 ? locationData.postcode.split(',')[0] : locationData.postcode;
    }

    this.connectionAddressFG = this.isParcelAddress
      ? this.prepareParcelAddressForm(locationData)
      : this.prepareConnectionAddressForm(locationData);

    if (
      this.environmentService.showBuildingType &&
      (this.showNewDevAreaDropdownForConnection ||
        this.showNewDevAreaDropdownForInstallation ||
        (this.currentInstallationFlow === this.installationFlowType.FeedInSystem && this.environmentService.showFeedInSystemBuildingType))
    ) {
      const defaultBuildingType = this.environmentService.hideDefaultBuildingType
        ? ''
        : this.buildingTypes.find(b => Number(b.order) === 1)?.value || '';
      const validators = this.environmentService.buildingTypeRequired ? [Validators.required] : [];
      this.connectionAddressFG.addControl(
        'buildingType',
        new FormControl(this.connectionData.buildingType || defaultBuildingType, validators)
      );
    }

    this.connectionAddressFG.addControl('customFields', new FormControl(this.connectionData.customFields));

    this.connectionAddressFG.valueChanges.subscribe(() =>
      this.personalDataService.updatePersonalDataForm(this.connectionAddressFG, 'connectionAddress')
    );

    this.personalDataService.updatePersonalDataForm(this.connectionAddressFG, 'connectionAddress');
  }

  isFieldRequired(field: string) {
    const formField = this.connectionAddressFG.get(field);
    return ValidationsService.isFieldRequired(formField);
  }

  private prepareParcelAddressForm(locationData: any) {
    return new FormGroup({
      swsDevAreaSubDistrict: new FormControl(
        {
          value: this.connectionData.swsDevAreaSubDistrict || locationData.swsDevAreaSubDistrict || '',
          disabled: !this.isReadOnly && !!(this.connectionData.swsDevAreaSubDistrict || locationData.swsDevAreaSubDistrict),
        },
        [Validators.required]
      ),
      swsParcel: new FormControl(
        {
          value: this.connectionData.swsParcel || locationData.swsParcel || '',
          disabled: !this.isReadOnly && !!(this.connectionData.swsParcel || locationData.swsParcel),
        },
        [Validators.required]
      ),
      swsParcelNumber: new FormControl(
        {
          value: this.connectionData.swsParcelNumber || locationData.swsParcelNumber || '',
          disabled: !this.isReadOnly && !!(this.connectionData.swsParcelNumber || locationData.swsParcelNumber),
        },
        [Validators.required]
      ),
      swsDevAreaName: new FormControl({
        value: this.connectionData.swsDevAreaName || locationData.swsDevAreaName || '',
        disabled: !this.isReadOnly && !!(this.connectionData.swsDevAreaName || locationData.swsDevAreaName),
      }),
    });
  }

  private prepareConnectionAddressForm(locationData: any): FormGroup {
    const form = new FormGroup({
      postCode: new FormControl({
        value: this.connectionData.postCode || locationData.postcode || '',
        disabled: !this.isReadOnly && !!(this.connectionData.postCode || locationData.postcode),
      }),
      city: new FormControl({
        value: this.connectionData.city || locationData.city || '',
        disabled: !this.isReadOnly && !!(this.connectionData.city || locationData.city),
      }),
      street: new FormControl({
        value: this.connectionData.street || locationData.street || '',
        disabled: !this.isReadOnly && !!(this.connectionData.street || locationData.street),
      }),
      streetNo: new FormControl({
        value: this.connectionData.streetNo || locationData.street_number || '000',
        disabled: !this.isReadOnly && !!(this.connectionData.streetNo || locationData.street_number),
      }),
    });

    if (this.environmentService.showDistrict) {
      form.addControl(
        'district',
        new FormControl({
          value: this.connectionData.district || locationData.district || '',
          disabled: !this.isReadOnly && !!(this.connectionData.district || locationData.district),
        })
      );
    }

    return form;
  }
}
